import React, { useState } from 'react';
import { 
  HelpCircle, MessageCircle, Phone, Mail, 
  Search, ChevronDown, ChevronRight, 
  FileText, Shield, CreditCard, Trophy,
  Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import Navbar from './Navbar';

const HelpSupport = () => {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'general', label: 'General', icon: HelpCircle },
    { id: 'account', label: 'Account & Profile', icon: Shield },
    { id: 'payments', label: 'Payments & Wallet', icon: CreditCard },
    { id: 'contests', label: 'Contests & Contests', icon: Trophy }
  ];

  const faqs = {
    general: [
      {
        question: "What is WinQuizz?",
        answer: "WinQuizz is a skill-based quiz platform where you can participate in various contests, test your knowledge, and win real money prizes. We offer different types of quizzes across multiple categories."
      },
      {
        question: "How do I get started?",
        answer: "To get started: 1) Create an account with your email/phone, 2) Complete your profile setup, 3) Add money to your wallet, 4) Browse available contests and join one that interests you!"
      },
      {
        question: "Is WinQuizz legal?",
        answer: "Yes, WinQuizz is completely legal as it's a skill-based platform. We comply with all applicable laws and regulations. However, please check your local jurisdiction's laws regarding online skill contests."
      },
      {
        question: "What are the age requirements?",
        answer: "You must be 18+ years old to participate in WinQuizz contests. We require age verification as part of our KYC process."
      },
      {
        question: "How do I contact support?",
        answer: "You can contact our support team via email (support@winquizz.com) or phone (+91 12345 67890). We typically respond within 2 hours."
      }
    ],
    account: [
      {
        question: "How do I verify my account?",
        answer: "Account verification involves: 1) Phone verification via OTP, 2) Email verification via OTP, 3) KYC verification with Aadhar and PAN details. Each step unlocks additional features and higher limits."
      },
      {
        question: "I forgot my password, how do I reset it?",
        answer: "Click on 'Forgot Password' on the login screen, enter your registered email/phone, and follow the instructions sent to you."
      },
      {
        question: "How do I update my profile information?",
        answer: "Go to Profile > Personal Information and click Edit. You can update your address, city, state, and other details. Some information like phone/email require re-verification if changed."
      },
      {
        question: "What is KYC and why is it required?",
        answer: "KYC (Know Your Customer) verification is required for withdrawals and involves submitting your Aadhar and PAN details. It ensures security and compliance with financial regulations."
      },
      {
        question: "What is the referral program?",
        answer: "Share your unique referral code with friends. When they sign up using your code, both you and your friend get special bonuses and benefits."
      }
    ],
    payments: [
      {
        question: "How do I add money to my wallet?",
        answer: "Go to Wallet > Add Money, choose your amount, and complete payment via our secure payment gateway. We support UPI, cards, net banking, and popular wallets."
      },
      {
        question: "What's the difference between deposited cash and bonus cash?",
        answer: "Deposited cash is money you add to your wallet and can be withdrawn. Bonus cash includes referral bonuses and promotional credits that can be used to join contests but cannot be withdrawn."
      },
      {
        question: "How do I withdraw my winnings?",
        answer: "Go to Wallet > Withdraw, enter your bank details, and submit the request. Withdrawals typically process within 1-3 business days. KYC verification is required for withdrawals."
      },
      {
        question: "Are there any withdrawal limits?",
        answer: "Minimum withdrawal is ₹100. Maximum depends on your verification level. Fully verified users can withdraw up to ₹50,000 per day."
      },
      {
        question: "What if my payment failed?",
        answer: "Failed payments are automatically refunded within 5-7 business days. If you don't receive the refund, contact our support team with the transaction details."
      },
      {
        question: "What payment methods do you support?",
        answer: "We support multiple secure payment options: Credit/Debit Cards (Visa, MasterCard, RuPay), Net Banking (all major banks), UPI & Digital Wallets (Paytm, PhonePe, Google Participate, Amazon Participate, Mobikwik). All transactions are processed through our secure payment gateway with bank-level encryption."
      },
      {
        question: "Is my payment information secure?",
        answer: "Yes, absolutely. We use industry-standard SSL encryption and PCI DSS compliant payment processing. Your card details are never stored on our servers and all transactions are processed through secure payment gateways."
      },
      {
        question: "Are there any payment processing fees?",
        answer: "No, we do not charge any additional fees for adding money to your wallet. However, your bank or payment provider may charge nominal transaction fees as per their terms."
      }
    ],
    contests: [
      {
        question: "How do contests work?",
        answer: "Choose a contest, participate with the entry fee, answer questions within the time limit, and compete for prizes based on your score and rank. Winners are determined by accuracy and speed."
      },
      {
        question: "When are contest results announced?",
        answer: "Results are typically announced within 1-2 hours after the contest ends. You'll receive a notification and can view detailed results in 'My Contests'."
      },
      {
        question: "What happens if I miss a live contest?",
        answer: "Once a contest starts, you cannot join late. Make sure to join before the start time. You can see upcoming contests and set reminders."
      },
      {
        question: "How are rankings determined?",
        answer: "Rankings are based on your final score (correct answers) and completion time. In case of ties, the participant who finished earlier gets the higher rank."
      },
      {
        question: "Can I play practice contests?",
        answer: "Yes! We offer practice contests where you can test your skills and get familiar with the format without any pressure."
      },
      {
        question: "What types of contests are available?",
        answer: "We offer various contest types including General Knowledge, Science, History, Sports, Entertainment, and more. New contest categories are added regularly."
      }
    ]
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "Send us your queries",
      action: "Send Email",
      available: "support@winquizz.com"
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "Call our helpline",
      action: "Call Now",
      available: "+91 12345 67890"
    }
  ];

  const filteredFaqs = faqs[selectedCategory].filter(faq =>
    searchQuery === '' || 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Help & Support</h1>
          <p className="text-gray-600">Find answers to your questions or contact our support team</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Search for help articles..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="space-y-1">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        selectedCategory === category.id 
                          ? 'bg-red-50 text-red-600 border-r-2 border-red-600' 
                          : 'text-gray-600'
                      }`}
                    >
                      <category.icon className="h-5 w-5" />
                      <span>{category.label}</span>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>


          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* FAQs */}
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <p className="text-gray-600">
                  {categories.find(c => c.id === selectedCategory)?.label} related questions
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredFaqs.length === 0 ? (
                    <div className="text-center py-8">
                      <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">
                        {searchQuery ? 'No FAQs found matching your search' : 'No FAQs available in this category'}
                      </p>
                    </div>
                  ) : (
                    filteredFaqs.map((faq, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          {expandedFaq === index ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                        {expandedFaq === index && (
                          <div className="px-4 pb-4 text-gray-600">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card>
              <CardHeader>
                <CardTitle>Still Need Help?</CardTitle>
                <p className="text-gray-600">Contact our support team for personalized assistance</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contactMethods.map((method, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-red-300 transition-colors">
                      <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-3">
                        <method.icon className="h-6 w-6 text-red-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-center mb-2">{method.title}</h3>
                      <p className="text-sm text-gray-600 text-center mb-3">{method.description}</p>
                      <p className="text-xs text-gray-500 text-center mb-3">{method.available}</p>
                      <Button className="w-full" variant="outline">
                        {method.action}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Resources */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <FileText className="h-6 w-6 text-blue-600" />
                    <div>
                      <h4 className="font-medium text-gray-900">Terms & Conditions</h4>
                      <p className="text-sm text-gray-600">Read our terms of service</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <Shield className="h-6 w-6 text-green-600" />
                    <div>
                      <h4 className="font-medium text-gray-900">Privacy Policy</h4>
                      <p className="text-sm text-gray-600">How we protect your data</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <Trophy className="h-6 w-6 text-yellow-600" />
                    <div>
                      <h4 className="font-medium text-gray-900">Fair Play Policy</h4>
                      <p className="text-sm text-gray-600">Our commitment to fairness</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <HelpCircle className="h-6 w-6 text-purple-600" />
                    <div>
                      <h4 className="font-medium text-gray-900">How to Participate Guide</h4>
                      <p className="text-sm text-gray-600">Step-by-step participation guide</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;